import React, { useState } from 'react';
import { sheetsClient } from '../../data/sheetsClient';
import { useTeacherData } from '../../context/TeacherDataContext';
import { Card, Button, Skeleton } from '../shared/UI';
import { formatCurrency } from '../../utils/feeCalculations';
import { eventYearFrom, formatEventDateRange } from '../../../shared/eventDate';

const PAYMENT_OPTIONS = [
  'Invoice required',
  'E-transfer',
  'Cheque',
];

const FIELD = (label, key, type = 'text', required = true, hint = '') => ({ label, key, type, required, hint });

const FIELDS = [
  FIELD('Estimated number of students attending', 'estAttendance', 'number'),
  FIELD('Estimated number of dance entries', 'estEntries', 'number'),
];

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  fontSize: 14,
  border: '1px solid #D5D5D5',
  borderRadius: 6,
  boxSizing: 'border-box',
  fontFamily: 'inherit',
};

const labelStyle = { fontSize: 13, fontWeight: 500, color: '#444', marginBottom: 4, display: 'block' };
const errorStyle = { fontSize: 12, color: '#D32F2F', marginTop: 3 };

export default function EventRegistration({ navigateTo }) {
  const {
    school, event, invoiceSummary, timeline,
    loading: { school: schoolLoading, invoiceSummary: invoiceLoading, timeline: timelineLoading },
    refetch,
  } = useTeacherData();
  // Pulled from INFORMATION_SHEET's Prices tab ("School Registration Fee"
  // row) via getInvoiceSummary — already fetched app-wide, and this form
  // already calls refetch.invoiceSummary() on submit (see handleSubmit).
  const registrationFee = invoiceSummary?.schoolRegistrationFee?.amount ?? 0;
  const currEvent = event?.[0];
  const eventYear = eventYearFrom(currEvent?.startDate);
  const eventDateRange = formatEventDateRange(currEvent?.startDate, currEvent?.endDate);

  const [form, setForm] = useState({
    estAttendance: '', estEntries: '', paymentMethod: '', acknowledged: false,
    firstTimeDancefest: '', needsNewPlaque: false,
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [registrationStatus, setRegistrationStatus] = useState(null);
  const [submitError, setSubmitError] = useState('');

  if (schoolLoading || invoiceLoading || timelineLoading) {
    return (
      <div style={{ padding: 20, maxWidth: 600, margin: '0 auto' }}>
        <Skeleton width={220} height={26} style={{ marginBottom: 10 }} />
        <Skeleton width={280} height={14} style={{ marginBottom: 20 }} />
        <Card title="School information">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} width="80%" height={14} style={{ marginBottom: 10 }} />
          ))}
        </Card>
        <Card title="Participation details">
          <Skeleton height={38} radius={6} style={{ marginBottom: 16 }} />
          <Skeleton height={38} radius={6} style={{ marginBottom: 16 }} />
          <Skeleton height={38} radius={6} />
        </Card>
      </div>
    );
  }

  const set = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(prev => ({ ...prev, [key]: value }));
    setErrors(prev => ({ ...prev, [key]: undefined }));
  };

  const validate = () => {
    const errs = {};
    if (!form.estAttendance) errs.estAttendance = 'Required';
    if (!form.estEntries) errs.estEntries = 'Required';
    if (!form.paymentMethod) errs.paymentMethod = 'Required';
    if (!form.firstTimeDancefest) errs.firstTimeDancefest = 'Required';
    if (!form.acknowledged) errs.acknowledged = 'You must acknowledge this statement to submit.';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSubmitting(true);
    setSubmitError('');
    try {
      const result = await sheetsClient.submitEventRegistration({ ...form, school });
      // Both needed for the Dashboard to show registration as complete right
      // away: invoiceSummary carries the fee amount, but the "Event
      // Registration" row's actual status (and the fees it unlocks) comes
      // from timeline — see Dashboard.js's isEventRegistered.
      refetch.invoiceSummary();
      refetch.timeline();
      setRegistrationStatus(result?.registrationStatus || 'Submitted');
      setSubmitted(true);
      window.scrollTo(0, 0);
    } catch (err) {
      setSubmitError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Already registered (any status) — show where things stand instead of
  // the form. The backend refuses a second registration either way; this
  // just saves the teacher filling it in for nothing.
  const eventRegRow = (timeline || []).find((step) => step.title === 'Event Registration');
  const existingStatus = eventRegRow && eventRegRow.status !== 'not-registered' ? eventRegRow.status : null;
  if (existingStatus && !submitted) {
    const message = {
      waitlisted: "Your school is on the event waitlist. You can't add participants or entries yet; OSSDF will let you know if space becomes available.",
      withdrawn: 'Your school has withdrawn from this event.',
      'fee-pending': 'Your registration has been received. The school registration fee is still outstanding — see Invoices for details.',
    }[existingStatus] || 'Your registration has been received and your school registration fee is paid.';
    return (
      <div style={{ padding: 20, maxWidth: 600, margin: '0 auto', textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>{existingStatus === 'waitlisted' ? '⏳' : '✓'}</div>
        <h2 style={{ fontSize: 20, fontWeight: 500, marginBottom: 8 }}>
          {school?.name || 'Your school'} is already registered{eventYear ? ` for OSS Dancefest ${eventYear}` : ''}
        </h2>
        <p style={{ fontSize: 14, color: '#666', marginBottom: 20 }}>
          {message} If anything about your registration needs to change, contact <a href="mailto:ossdancefest@gmail.com" style={{ color: '#185FA5' }}>ossdancefest@gmail.com</a>.
        </p>
        <Button onClick={() => navigateTo('dashboard')}>Back to dashboard</Button>
      </div>
    );
  }

  if (submitted) {
    const waitlisted = registrationStatus === 'Waitlisted';
    return (
      <div style={{ padding: 20, maxWidth: 600, margin: '0 auto', textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>{waitlisted ? '⏳' : '✓'}</div>
        <h2 style={{ fontSize: 20, fontWeight: 500, marginBottom: 8 }}>
          {waitlisted ? "You're on the waitlist" : 'Registration submitted'}
        </h2>
        <p style={{ fontSize: 14, color: '#666', marginBottom: 20 }}>
          {waitlisted ? (
            <>
              Your event registration for <strong>{school?.name}</strong> has been received, but the event has
              reached capacity — your school has been placed on the waitlist. You can't add participants or
              entries yet; OSSDF will let you know if space becomes available.
            </>
          ) : (
            <>
              Your event registration for <strong>{school?.name}</strong> has been received.
              You will be contacted regarding payment.
            </>
          )}
        </p>
        <Button onClick={() => navigateTo('dashboard')}>Back to dashboard</Button>
      </div>
    );
  }

  return (
    <div style={{ padding: 20, maxWidth: 600, margin: '0 auto' }}>
      <h1 style={{ fontSize: 22, fontWeight: 500, marginBottom: 4 }}>Event Registration</h1>
      <p style={{ fontSize: 14, color: '#666', marginBottom: 20 }}>
        OSS Dancefest{eventYear ? ` ${eventYear}` : ''} · complete your school's event registration
      </p>

      {/* Prefilled school info — read only */}
      <Card title="School information" subtitle="Pulled from your account — contact OSSDF to update">
        {[
          ['School', school?.name],
          ['Address', school?.address],
          ['School Board', school?.schoolBoard],
          ['Principal', school?.principalName],
          ['Contact', school?.registrantName],
          ['Email', school?.registrantEmail],
          ['Phone', school?.cellPhone],
        ].map(([label, value]) => value ? (
          <div key={label} style={{ display: 'flex', gap: 8, fontSize: 14, marginBottom: 6 }}>
            <span style={{ color: '#666', minWidth: 110 }}>{label}</span>
            <span>{value}</span>
          </div>
        ) : null)}
      </Card>

      {/* Fields to fill in — one continuous form spanning both cards below,
          all the way through the submit button. */}
      <form onSubmit={handleSubmit}>
        <Card title="Participation details">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {FIELDS.map(({ label, key, type }) => (
              <div key={key}>
                <label style={labelStyle}>{label} *</label>
                <input
                  type={type}
                  min={type === 'number' ? 0 : undefined}
                  value={form[key]}
                  onChange={set(key)}
                  style={{ ...inputStyle, borderColor: errors[key] ? '#D32F2F' : '#D5D5D5' }}
                />
                {errors[key] && <div style={errorStyle}>{errors[key]}</div>}
              </div>
            ))}

            <div>
              <label style={labelStyle}>Is this your first time at Dancefest? *</label>
              <select
                value={form.firstTimeDancefest}
                onChange={e => {
                  const value = e.target.value;
                  // The plaque checkbox only shows for a returning school — if
                  // they're switching to "Yes" (first time), clear any answer
                  // so a stale "needs a new plaque" from before this switch
                  // never gets silently submitted.
                  setForm(prev => ({ ...prev, firstTimeDancefest: value, needsNewPlaque: value === 'Yes' ? false : prev.needsNewPlaque }));
                  setErrors(prev => ({ ...prev, firstTimeDancefest: undefined }));
                }}
                style={{ ...inputStyle, borderColor: errors.firstTimeDancefest ? '#D32F2F' : '#D5D5D5' }}
              >
                <option value="" disabled>Select an answer</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
              {errors.firstTimeDancefest && <div style={errorStyle}>{errors.firstTimeDancefest}</div>}
            </div>

            {form.firstTimeDancefest === 'No' && (
              <div>
                <label style={{ ...labelStyle, display: 'flex', alignItems: 'flex-start', gap: 8, fontWeight: 400, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={form.needsNewPlaque}
                    onChange={set('needsNewPlaque')}
                    style={{ marginTop: 2, flexShrink: 0 }}
                  />
                  <span>Do you need a new wooden plaque for your plates? (i.e., is your existing plaque full?)</span>
                </label>
              </div>
            )}
          </div>
        </Card>

        <Card title="Registration Information">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ background: '#F5F5F5', borderRadius: 8, padding: 14, fontSize: 13, color: '#444' }}>
              Payment of {registrationFee != null ? `$${registrationFee.toFixed(2)}` : 'the required fee'} provides your school with access to professional development,
              networking with other Ontario dance educators at our OSSDF Teachers' Council Meetings,
              and registration to DANCEFEST, our annual dance celebration{eventDateRange ? ` from ${eventDateRange}` : ''}. Number of schools and dancers are limited due to event capacity — register early!
            </div>

            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 6 }}>Payment options:</div>
              <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: '#444' }}>
                <li>Cheque payable to Ontario Secondary School DANCEFEST, mailed to c/o Cathy Bowman, 9 Nithview Court, New Hamburg, Ontario N3A 2H4</li>
                <li>E-transfer to ossdfsecretary@gmail.com</li>
                <li>Arrangement for approved vendor status with your school board</li>
              </ul>
            </div>

            <div style={{ fontSize: 13, color: '#444' }}>
              For further information, email Cathy Bowman at ossdfsecretary@gmail.com.
              We look forward to welcoming you to the OSSDF community!
            </div>

            <div>
              <label style={labelStyle}>Preferred form of payment *</label>
              <select
                value={form.paymentMethod}
                onChange={set('paymentMethod')}
                style={{ ...inputStyle, borderColor: errors.paymentMethod ? '#D32F2F' : '#D5D5D5' }}
              >
                <option value="" disabled>Select payment method</option>
                {PAYMENT_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              {errors.paymentMethod && <div style={errorStyle}>{errors.paymentMethod}</div>}
            </div>

            <div>
              <label style={{ ...labelStyle, display: 'flex', alignItems: 'flex-start', gap: 8, fontWeight: 400, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={form.acknowledged}
                  onChange={set('acknowledged')}
                  style={{ marginTop: 2, flexShrink: 0 }}
                />
                <span>
                  I acknowledge that my school is registering to participate in the OSSDF Awards
                  competition and that all information provided is accurate. I understand that a
                  {' '}{formatCurrency(registrationFee)} school registration fee is required and that registration is subject to
                  available capacity.
                </span>
              </label>
              {errors.acknowledged && <div style={errorStyle}>{errors.acknowledged}</div>}
            </div>

            {submitError && (
              <div style={{ ...errorStyle, fontSize: 13 }}>{submitError}</div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 4 }}>
              <Button variant="default" onClick={() => navigateTo('dashboard')} type="button">Cancel</Button>
              <Button variant="primary" type="submit" disabled={submitting}>
                {submitting ? 'Submitting…' : 'Submit registration'}
              </Button>
            </div>
          </div>
        </Card>
      </form>
    </div>
  );
}
